import { useMemo } from "react";
import { Bot, Eye, GitBranch, GitCompare, GitMerge, MoreHorizontal, Radio, RotateCcw, Rocket, Tag, Undo2 } from "lucide-react";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Skeleton, cn } from "@ncp/ui";
import type { CommitView } from "../types";

const ROW = 56;
const LANE = 14;
const COLORS = ["#b68a35", "#2f6f9f", "#16a34a", "#9f4f2f", "#7c5cbf", "#c2410c", "#0f766e"];

interface Placed { commit: CommitView; row: number; lane: number }

/** Lanes for a git-style graph: each open line waits for the parent it will reach; merges converge into the commit that both lines reach. */
export function layoutHistory(commits: CommitView[]) {
  const lanes: (string | null)[] = [];
  const placed: Placed[] = [];
  const at = new Map<string, Placed>();
  commits.forEach((c, row) => {
    let lane = lanes.indexOf(c.id);
    if (lane < 0) { lane = lanes.indexOf(null); if (lane < 0) { lane = lanes.length; lanes.push(null); } }
    for (let i = 0; i < lanes.length; i++) if (i !== lane && lanes[i] === c.id) lanes[i] = null;
    lanes[lane] = c.parentId ?? null;
    if (c.mergeParentId && !lanes.includes(c.mergeParentId)) {
      let free = lanes.indexOf(null);
      if (free < 0) { free = lanes.length; lanes.push(null); }
      lanes[free] = c.mergeParentId;
    }
    const p = { commit: c, row, lane };
    placed.push(p);
    at.set(c.id, p);
  });
  const links: Array<{ from: Placed; to: Placed; merge: boolean }> = [];
  for (const p of placed) {
    if (p.commit.parentId && at.has(p.commit.parentId)) links.push({ from: p, to: at.get(p.commit.parentId)!, merge: false });
    if (p.commit.mergeParentId && at.has(p.commit.mergeParentId)) links.push({ from: p, to: at.get(p.commit.mergeParentId)!, merge: true });
  }
  return { placed, links, width: Math.max(1, ...placed.map((p) => p.lane + 1)) };
}

export interface HistoryActions {
  onView: (id: string) => void;
  onCompare: (from: string, to: string) => void;
  onRestore?: (id: string) => void;
  onRevert?: (id: string) => void;
  onBranchFrom?: (id: string) => void;
  onTag?: (id: string) => void;
  onPublish?: (id: string) => void;
}

export function HistoryPanel({ commits, loading, viewing, headId, liveCommitId, mainCommits, actions }: {
  commits: CommitView[]; loading: boolean; viewing: string | null; headId: string; liveCommitId?: string | null; mainCommits: Set<string>; actions: HistoryActions;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { placed, links, width } = useMemo(() => layoutHistory(commits), [commits]);
  const svgW = width * LANE + 12;
  const x = (lane: number) => 8 + lane * LANE;
  const y = (row: number) => row * ROW + 22;

  if (loading) return <div className="space-y-2 p-3">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div className="relative">
      <svg className="pointer-events-none absolute start-0 top-0" width={svgW} height={placed.length * ROW} style={{ direction: "ltr" }}>
        {links.map(({ from, to, merge }, i) => {
          const x1 = x(from.lane), y1 = y(from.row), x2 = x(to.lane), y2 = y(to.row);
          const bend = y1 + ROW * 0.6;
          const d = x1 === x2 ? `M${x1},${y1} L${x2},${y2}` : merge ? `M${x1},${y1} C${x1},${bend} ${x2},${y1 + 4} ${x2},${bend} L${x2},${y2}` : `M${x1},${y1} L${x1},${y2 - ROW * 0.6} C${x1},${y2 - 6} ${x2},${y2 - ROW * 0.5} ${x2},${y2}`;
          return <path key={i} d={d} fill="none" stroke={COLORS[(merge ? to.lane : from.lane) % COLORS.length]} strokeWidth={2} strokeOpacity={0.75} />;
        })}
        {placed.map((p) => (
          <g key={p.commit.id}>
            {p.commit.isLive ? <circle cx={x(p.lane)} cy={y(p.row)} r={9} fill="none" stroke="#16a34a" strokeWidth={2} /> : null}
            <circle cx={x(p.lane)} cy={y(p.row)} r={p.commit.mergeParentId ? 5.5 : 4.5} fill={p.commit.source === "Ai" ? "#2f6f9f" : COLORS[p.lane % COLORS.length]} stroke="white" strokeWidth={2} />
          </g>
        ))}
      </svg>
      <ol>
        {placed.map(({ commit: c }) => (
          <li key={c.id} className={cn("group relative flex items-start gap-2 border-b border-sand-100 pe-2 transition hover:bg-sand-50", viewing === c.id && "bg-aegold-50/70")} style={{ height: ROW, paddingInlineStart: svgW + 4 }}>
            <button type="button" className="min-w-0 flex-1 py-1.5 text-start" onClick={() => actions.onView(c.id)}>
              <div className="flex items-center gap-1 overflow-hidden">
                {c.heads.map((h) => <span key={h} className={cn("inline-flex shrink-0 items-center gap-0.5 rounded px-1 text-[10px] font-semibold", h === "main" ? "bg-aeblack-900 text-white" : "bg-aegold-100 text-aegold-900")}><GitBranch className="size-2.5" />{h}</span>)}
                {c.isLive ? <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-success-600 px-1 text-[10px] font-bold text-white"><Radio className="size-2.5" />LIVE</span> : null}
                {c.tags.map((tag) => <span key={tag} className="inline-flex shrink-0 items-center gap-0.5 rounded border border-sand-300 px-1 text-[10px] text-sand-700"><Tag className="size-2.5" />{tag}</span>)}
                {c.source === "Ai" ? <Bot className="size-3 shrink-0 text-seablue-600" /> : c.source === "Merge" ? <GitMerge className="size-3 shrink-0 text-sand-500" /> : null}
                <span className="truncate text-xs font-medium text-aeblack-900">{c.message}</span>
              </div>
              <p className="truncate text-[10.5px] text-muted-foreground">{c.authorName} · {formatDateTime(c.createdAt, locale)} · <span className="font-mono">{c.hash.slice(0, 7)}</span>{c.summary ? ` · ${c.summary}` : ""}</p>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button size="icon-sm" variant="ghost" className="mt-2 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"><MoreHorizontal /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => actions.onView(c.id)}><Eye /> {t("admin:builder.history.view")}</DropdownMenuItem>
                {c.id !== headId ? <DropdownMenuItem onSelect={() => actions.onCompare(c.id, headId)}><GitCompare /> {t("admin:builder.history.compareHead")}</DropdownMenuItem> : null}
                {c.parentId ? <DropdownMenuItem onSelect={() => actions.onCompare(c.parentId!, c.id)}><GitCompare /> {t("admin:builder.history.showChanges")}</DropdownMenuItem> : null}
                {liveCommitId && c.id !== liveCommitId ? <DropdownMenuItem onSelect={() => actions.onCompare(liveCommitId, c.id)}><Radio /> {t("admin:builder.history.compareLive")}</DropdownMenuItem> : null}
                {actions.onBranchFrom || actions.onRestore || actions.onRevert || actions.onTag ? <DropdownMenuSeparator /> : null}
                {actions.onBranchFrom ? <DropdownMenuItem onSelect={() => actions.onBranchFrom!(c.id)}><GitBranch /> {t("admin:builder.history.branchFrom")}</DropdownMenuItem> : null}
                {actions.onRestore && c.id !== headId ? <DropdownMenuItem onSelect={() => actions.onRestore!(c.id)}><RotateCcw /> {t("admin:builder.history.restore")}</DropdownMenuItem> : null}
                {actions.onRevert && c.parentId ? <DropdownMenuItem onSelect={() => actions.onRevert!(c.id)}><Undo2 /> {t("admin:builder.history.revert")}</DropdownMenuItem> : null}
                {actions.onTag ? <DropdownMenuItem onSelect={() => actions.onTag!(c.id)}><Tag /> {t("admin:builder.history.tag")}</DropdownMenuItem> : null}
                {actions.onPublish && mainCommits.has(c.id) && !c.isLive ? <><DropdownMenuSeparator /><DropdownMenuItem onSelect={() => actions.onPublish!(c.id)}><Rocket /> {t("admin:builder.history.publish")}</DropdownMenuItem></> : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        ))}
      </ol>
      {commits.length === 0 ? <p className="p-4 text-center text-xs text-muted-foreground">{t("admin:builder.history.empty")}</p> : null}
      <div className="flex flex-wrap gap-2 p-3 text-[10px] text-muted-foreground">
        <Badge size="sm" variant="success"><Radio /> {t("admin:builder.history.legendLive")}</Badge>
        <Badge size="sm" variant="ai"><Bot /> {t("admin:builder.history.legendAi")}</Badge>
        <Badge size="sm" variant="outline"><GitMerge /> {t("admin:builder.history.legendMerge")}</Badge>
      </div>
    </div>
  );
}

/** Commits reachable from a head (first and merge parents). */
export function reachable(commits: CommitView[], head: string | null | undefined) {
  const byId = new Map(commits.map((c) => [c.id, c]));
  const seen = new Set<string>();
  const stack = head ? [head] : [];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const c = byId.get(id);
    if (c?.parentId) stack.push(c.parentId);
    if (c?.mergeParentId) stack.push(c.mergeParentId);
  }
  return seen;
}
